import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, FileUp, Download, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { parseDelimited } from '@/utils/csv-helper';
import { useAuth, can } from '@/hooks/useAuth';

/**
 * Upload a whole plan's courier rates from a spreadsheet.
 *
 * One file serves both directions. The download already lists EVERY active courier for that service,
 * with the ones already priced filled in — so adding five couriers is five lines typed into a sheet
 * that is otherwise correct, rather than five passes through a form. Blank rows are left blank on
 * purpose and are skipped on the way back in.
 *
 * Nothing is written until the preview has been seen: "check the file" runs the exact same import
 * with `dryRun`, so the counts shown are the counts that will happen.
 */
interface ImportResult { created: number; updated: number; skipped: number; errors: string[]; couriers: string[] }

export function ImportRatesDialog({ slabId, slabName, onClose }: { slabId: number; slabName: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [serviceType, setServiceType] = useState<'B2C' | 'B2B'>('B2C');
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [checked, setChecked] = useState(false);

  const run = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const { rows } = parseDelimited(text, { mapHeader: (h) => h.trim() });
      if (!rows.length) throw new Error('That file has no rows in it.');
      return (await api.post<ImportResult>(`/pricing-slabs/${slabId}/rates/import`, { serviceType, rows, dryRun })).data;
    },
    onSuccess: (data, dryRun) => {
      setResult(data);
      setChecked(true);
      if (!dryRun) {
        qc.invalidateQueries({ queryKey: ['pricing-slab', slabId] });
        qc.invalidateQueries({ queryKey: ['pricing-slabs'] });
        toast.success(`${data.created} added, ${data.updated} updated`);
        onClose();
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Could not read that file'),
  });

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setChecked(false);
    const reader = new FileReader();
    reader.onload = (ev) => setText(String(ev.target?.result ?? ''));
    reader.readAsText(file);
  };

  const { user } = useAuth();
  const canExport = can(user, 'Billing Plans', 'All', 'export');
  const getSheet = () =>
    downloadBlob(
      `/pricing-slabs/${slabId}/rates/sheet?serviceType=${serviceType}`,
      `billing-plan-${slabId}-${serviceType}-rates.csv`,
    ).catch(() => toast.error('Could not download the sheet'));

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800">Upload courier rates</h3>
            <p className="text-xs text-slate-500">{slabName}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Service</label>
              <select value={serviceType}
                onChange={(e) => { setServiceType(e.target.value as 'B2C' | 'B2B'); setResult(null); setChecked(false); }}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
                <option value="B2C">B2C</option>
                <option value="B2B">B2B</option>
              </select>
            </div>
            {canExport && (
            <button onClick={getSheet}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
              <Download size={14} /> Download {serviceType} sheet
            </button>
            )}
          </div>

          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3 leading-relaxed">
            The sheet lists <b>every active {serviceType} courier</b>, with the ones already on this plan filled in.
            Type prices into the rows you want and upload it back — rows left blank are ignored.
            {serviceType === 'B2B' && (
              <> B2B has <b>one row per courier per zone</b> (Central, E, N1, N2, NE, S1, S2, W1, W2); the charges
              on the right are typed once, on that courier's first row.</>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Your filled-in file</label>
            <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 text-sm">
              <FileUp size={15} className="text-slate-500" />
              <span className={fileName ? 'text-slate-800' : 'text-slate-400'}>{fileName || 'Choose a .csv file…'}</span>
              <input type="file" accept=".csv,text/csv" onChange={pickFile} className="hidden" />
            </label>
          </div>

          {result && (
            <div className={`rounded-lg p-3 text-sm border ${result.errors.length ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="flex items-center gap-2 font-medium">
                {result.errors.length ? <AlertCircle size={15} className="text-amber-600" /> : <CheckCircle2 size={15} className="text-emerald-600" />}
                {checked && !run.isPending ? 'This is what will happen:' : 'Done:'}
              </div>
              <div className="text-xs mt-1.5 text-slate-700">
                <b>{result.created}</b> courier{result.created === 1 ? '' : 's'} added,{' '}
                <b>{result.updated}</b> updated, {result.skipped} row(s) left alone.
              </div>
              {!!result.couriers.length && (
                <div className="text-[11px] text-slate-600 mt-1">{result.couriers.join(', ')}</div>
              )}
              {!!result.errors.length && (
                <ul className="text-[11px] text-amber-800 mt-2 list-disc pl-4 space-y-0.5 max-h-28 overflow-y-auto">
                  {result.errors.slice(0, 12).map((e, i) => <li key={i}>{e}</li>)}
                  {result.errors.length > 12 && <li>…and {result.errors.length - 12} more</li>}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button disabled={!text.trim() || run.isPending} onClick={() => run.mutate(true)}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm disabled:opacity-50 flex items-center gap-2">
              {run.isPending && <Loader2 size={14} className="animate-spin" />} Check the file
            </button>
            <button disabled={!text.trim() || !checked || run.isPending} onClick={() => run.mutate(false)}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50">
              Import
            </button>
          </div>
          {!checked && !!text.trim() && (
            <p className="text-[11px] text-slate-500 text-right">Check the file first — nothing is saved until you press Import.</p>
          )}
        </div>
      </div>
    </div>
  );
}
